import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'zignal-landscape/lib/Modal';
import ModalHeader from 'zignal-landscape/lib/ModalHeader';
import ModalBody from 'zignal-landscape/lib/ModalBody';
import ModalFooter from 'zignal-landscape/lib/ModalFooter';
import Button from 'zignal-landscape/lib/Button';
import Input from 'zignal-landscape/lib/Input';
import Loading from 'zignal-landscape/lib/Loading';
import Label from 'zignal-landscape/lib/Label';

import {
  LAT_MIN,
  LAT_MAX,
  LON_MIN,
  LON_MAX,
  LATLONG_WARNING,
  buildPatchBody,
} from '../../constants';
import {
  isLatValid,
  isLonValid,
  isFiniteNumber,
  filterNumeric,
  disabledCursor,
  formatGeoResult,
} from '../../defaultLocationsUtils';

const INPUT_HEIGHT = '1.75rem';
const MODAL_WIDTH = '19.2rem';

const EditDefaultLocationModal = ({
  isOpen = false,
  onClose,
  onSubmit,
  onLookupForward,
  onLookupReverse,
  onClearLookup,
  location = null,
  lookupResults = [],
  isLookingUp = false,
  lookupError = null,
  isPatching = false,
  patchError = null,
}) => {
  const [cityQuery, setCityQuery] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [selectedGeoIdx, setSelectedGeoIdx] = useState(-1);

  // Pre-fill lat/lon from the existing record on open.
  useEffect(() => {
    if (!isOpen || !location || !location.location) {return;}
    if (location.location.latlong) {
      const ll = location.location.latlong;
      setLatitude(ll.lat !== undefined && ll.lat !== null ? String(ll.lat) : '');
      setLongitude(ll.lon !== undefined && ll.lon !== null ? String(ll.lon) : '');
    }
  }, [isOpen, location]);

  // Reset transient state when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      setCityQuery('');
      setLatitude('');
      setLongitude('');
      setSelectedGeoIdx(-1);
      onClearLookup && onClearLookup();
    }
  }, [isOpen, onClearLookup]);

  // A new lookupResults array invalidates the previous selection. Auto-select if exactly one.
  useEffect(() => {
    setSelectedGeoIdx(lookupResults.length === 1 ? 0 : -1);
  }, [lookupResults]);

  const hasLatLon = latitude !== '' || longitude !== '';
  const latLonNumeric = isFiniteNumber(latitude) && isFiniteNumber(longitude);
  const latLonInRange = isLatValid(latitude) && isLonValid(longitude);
  const latLonReady = latLonNumeric && latLonInRange;
  const latRangeError = latitude !== '' && isFiniteNumber(latitude) && !isLatValid(latitude);
  const lonRangeError = longitude !== '' && isFiniteNumber(longitude) && !isLonValid(longitude);
  const cityHasText = cityQuery.trim().length > 0;
  const canSearch = !isLookingUp && (
    (cityHasText && !hasLatLon) ||
    (latLonReady && !cityHasText)
  );

  const selectedGeo = selectedGeoIdx >= 0 && selectedGeoIdx < lookupResults.length
    ? lookupResults[selectedGeoIdx]
    : null;
  const canProceed = !!selectedGeo;

  const runSearch = () => {
    if (!canSearch) {return;}
    setSelectedGeoIdx(-1);
    if (cityHasText) {
      onLookupForward(cityQuery.trim());
    } else {
      onLookupReverse(Number(latitude), Number(longitude));
    }
  };

  const handleCityKeyDown = (e) => {
    if (e.key === 'Enter' && canSearch && cityHasText) {runSearch();}
  };

  const invalidatePicker = () => {
    if (lookupResults.length > 0 || selectedGeoIdx >= 0) {
      setSelectedGeoIdx(-1);
      onClearLookup && onClearLookup();
    }
  };

  const handleClose = () => {
    setCityQuery('');
    setLatitude('');
    setLongitude('');
    setSelectedGeoIdx(-1);
    onClose();
  };

  const handleUpdate = () => {
    if (!canProceed) {return;}
    onSubmit(location._id, buildPatchBody(selectedGeo));
  };

  return (
    <Modal
      isOpen={isOpen}
      size="small"
      onEscapePress={handleClose}
      onBackdropClick={handleClose}
      style={{maxWidth: MODAL_WIDTH, width: MODAL_WIDTH}}
    >
      <ModalHeader hideCloseButton={false} onClose={handleClose}>
        Edit Default Location
      </ModalHeader>

      <ModalBody>
        <div>
          {/* Carmen PATCH only accepts location.* fields; term/platform/field are read-only here. */}
          {location && (
            <div style={{marginBottom: '16px'}}>
              <div style={{marginBottom: '12px'}}>
                <Label>Term</Label>
                <p style={{margin: '4px 0', color: '#666'}}>{location.term}</p>
              </div>
              <div style={{marginBottom: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                <div>
                  <Label>Platform</Label>
                  <p style={{margin: '4px 0', color: '#666'}}>{location.platform}</p>
                </div>
                <div>
                  <Label>Field</Label>
                  <p style={{margin: '4px 0', color: '#666'}}>{location.story_field}</p>
                </div>
              </div>
            </div>
          )}

          <Label>New Location</Label>
          <div
            style={{
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '12px',
              backgroundColor: '#fafafa',
            }}
          >
            <div style={{marginBottom: '8px', ...disabledCursor(hasLatLon)}}>
              <Input
                id="dl-edit-location-city"
                placeholder="e.g. San Francisco, CA"
                value={cityQuery}
                onChange={(e) => { setCityQuery(e.target.value); invalidatePicker(); }}
                onKeyDown={handleCityKeyDown}
                disabled={hasLatLon}
                style={disabledCursor(hasLatLon)}
              />
            </div>

            <div style={{margin: '8px 0', color: '#666', fontSize: '0.9em'}}>Or</div>

            <div style={{display: 'grid', gridTemplateColumns: 'minmax(0, 7.2em) minmax(0, 7.2em)', gap: '8px', alignItems: 'end'}}>
              <div style={disabledCursor(cityHasText)}>
                <Label>Latitude</Label>
                <Input
                  id="dl-edit-location-lat"
                  type="text"
                  inputMode="decimal"
                  value={latitude}
                  onChange={(e) => { setLatitude(filterNumeric(e.target.value, latitude)); invalidatePicker(); }}
                  disabled={cityHasText}
                  style={{height: INPUT_HEIGHT, boxSizing: 'border-box', ...disabledCursor(cityHasText)}}
                />
              </div>
              <div style={disabledCursor(cityHasText)}>
                <Label>Longitude</Label>
                <Input
                  id="dl-edit-location-lon"
                  type="text"
                  inputMode="decimal"
                  value={longitude}
                  onChange={(e) => { setLongitude(filterNumeric(e.target.value, longitude)); invalidatePicker(); }}
                  disabled={cityHasText}
                  style={{height: INPUT_HEIGHT, boxSizing: 'border-box', ...disabledCursor(cityHasText)}}
                />
              </div>
            </div>

            {(latRangeError || lonRangeError) && (
              <p style={{margin: '8px 0 0', color: '#d9534f', fontSize: '0.85em'}}>
                {latRangeError && `Latitude must be between ${LAT_MIN} and ${LAT_MAX}. `}
                {lonRangeError && `Longitude must be between ${LON_MIN} and ${LON_MAX}.`}
              </p>
            )}

            {hasLatLon && (
              <p style={{margin: '8px 0 0', color: '#8a6d3b', fontSize: '0.85em'}}>
                {LATLONG_WARNING}
              </p>
            )}

            <div style={{marginTop: '12px'}}>
              <Button
                buttonStyle="primary"
                disabled={!canSearch}
                onClick={runSearch}
                style={disabledCursor(!canSearch)}
              >
                Search
              </Button>
            </div>
          </div>

          {isLookingUp && (
            <div style={{textAlign: 'center', padding: '8px'}}>
              <Loading />
            </div>
          )}

          {!isLookingUp && lookupError && (
            <p style={{margin: '8px 0', color: '#d9534f', fontSize: '0.85em'}}>{lookupError}</p>
          )}

          {!isLookingUp && lookupResults.length > 0 && (
            <ul style={{listStyle: 'none', padding: 0, margin: '8px 0', maxHeight: '160px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px'}}>
              {lookupResults.map((geo, idx) => (
                <li key={idx} style={{padding: '6px 10px', borderBottom: idx < lookupResults.length - 1 ? '1px solid #eee' : 'none'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                    <input
                      type="radio"
                      name="dl-edit-geo-result"
                      checked={selectedGeoIdx === idx}
                      onChange={() => setSelectedGeoIdx(idx)}
                    />
                    <span>{formatGeoResult(geo)}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}

          {patchError && typeof patchError === 'object' && patchError.fieldErrors && Object.keys(patchError.fieldErrors).map((field) => (
            <p key={field} style={{margin: '4px 0 0', color: '#d9534f', fontSize: '0.85em'}}>
              <strong>{field}:</strong> {patchError.fieldErrors[field]}
            </p>
          ))}

          {typeof patchError === 'string' && patchError && (
            <p style={{color: '#d9534f', marginTop: '8px'}}>{patchError}</p>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div style={{display: 'flex', gap: '8px'}}>
            <Button
              buttonStyle="positive"
              disabled={!canProceed || isPatching}
              onClick={handleUpdate}
              style={disabledCursor(!canProceed || isPatching)}
            >
              {isPatching ? 'Updating...' : 'Update'}
            </Button>
            <Button buttonStyle="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

EditDefaultLocationModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onLookupForward: PropTypes.func.isRequired,
  onLookupReverse: PropTypes.func.isRequired,
  onClearLookup: PropTypes.func,
  location: PropTypes.shape({
    _id: PropTypes.string,
    term: PropTypes.string,
    platform: PropTypes.string,
    story_field: PropTypes.string,
    location: PropTypes.shape({
      latlong: PropTypes.shape({
        lat: PropTypes.number,
        lon: PropTypes.number,
      }),
    }),
  }),
  lookupResults: PropTypes.array,
  isLookingUp: PropTypes.bool,
  lookupError: PropTypes.string,
  isPatching: PropTypes.bool,
  patchError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default EditDefaultLocationModal;
